import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collection, query, where, limit, getDocs, doc, updateDoc } from 'firebase/firestore';
import { Card, Button } from '../UI';
import { ShoppingCart, Check, Circle, Package, LogIn } from 'lucide-react';
import { db } from '../../services/firebase';
import { useAuth } from '../../hooks/useAuth';

const JOIN_LIST_URL = 'https://us-central1-nutrius-production.cloudfunctions.net/joinSharedList';

const SharedListView = () => {
  const { shareToken } = useParams();
  const { user, loading: authLoading } = useAuth();

  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditor, setIsEditor] = useState(false);

  useEffect(() => {
    if (authLoading) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        if (user) {
          // Signed in -- join as editor via the Cloud Function first (bridges
          // the Firestore rules gap: reading via shareToken is open to anyone,
          // but editing requires already being in sharedWith).
          const idToken = await user.getIdToken();
          const res = await fetch(JOIN_LIST_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
            body: JSON.stringify({ shareToken }),
          });
          const data = await res.json();

          if (!res.ok) {
            if (!cancelled) {
              setError(data.error === 'list_not_found'
                ? 'This list could not be found.'
                : 'Could not open this shared list.');
              setLoading(false);
            }
            return;
          }

          const snap = await getDocs(query(
            collection(db, 'groceryLists'),
            where('shareToken', '==', shareToken),
            limit(1)
          ));
          if (!cancelled) {
            if (snap.empty) {
              setError('This list could not be found.');
            } else {
              const d = snap.docs[0];
              setList({ id: d.id, ...d.data() });
              setIsEditor(true);
            }
            setLoading(false);
          }
        } else {
          // Guest -- read-only, no join call (editing requires an account).
          const snap = await getDocs(query(
            collection(db, 'groceryLists'),
            where('shareToken', '==', shareToken),
            limit(1)
          ));
          if (!cancelled) {
            if (snap.empty) {
              setError('This list could not be found.');
            } else {
              const d = snap.docs[0];
              setList({ id: d.id, ...d.data() });
              setIsEditor(false);
            }
            setLoading(false);
          }
        }
      } catch (err) {
        console.error('SharedListView error:', err);
        if (!cancelled) {
          setError('Something went wrong loading this list.');
          setLoading(false);
        }
      }
    };

    load();
    return () => { cancelled = true; };
  }, [shareToken, user, authLoading]);

  const toggleItem = async (itemId) => {
    if (!isEditor || !list) return;
    const items = (list.items || []).map(item =>
      item.id === itemId ? { ...item, isChecked: !item.isChecked } : item
    );
    setList({ ...list, items });
    try {
      await updateDoc(doc(db, 'groceryLists', list.id), { items });
    } catch (err) {
      console.error('toggleItem error:', err);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F3EF]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-[#2D5F4D]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F3EF] px-6">
        <Card className="max-w-sm text-center p-xl">
          <ShoppingCart size={36} className="text-text-tertiary mx-auto mb-lg" />
          <h2 className="text-title-3 text-primary mb-sm">{error}</h2>
          <Link to="/home" className="text-callout text-primary underline">Go to Nutrius</Link>
        </Card>
      </div>
    );
  }

  const items = list?.items || [];
  const uncheckedItems = items.filter(i => !i.isChecked);
  const checkedItems = items.filter(i => i.isChecked);

  return (
    <div className="min-h-screen bg-[#F5F3EF] px-4 py-8">
      <div className="max-w-lg mx-auto space-y-lg">
        <div>
          <h1 className="text-display font-rounded text-primary mb-sm">{list?.name || 'Shared List'}</h1>
          <p className="text-body text-text-secondary">
            {isEditor ? 'You can edit this list' : 'Shared with you -- read only'}
          </p>
        </div>

        {!isEditor && (
          <Card className="bg-gradient-to-br from-accent/5 to-primary/5 border-accent/20 p-lg">
            <div className="flex items-center justify-between gap-lg">
              <div>
                <p className="text-callout font-semibold text-text-primary mb-xs">Sign in to edit this list</p>
                <p className="text-caption text-text-secondary">Check off items and add your own.</p>
              </div>
              <Button
                variant="primary"
                icon={<LogIn size={18} />}
                size="small"
                onClick={() => { window.location.href = '/login'; }}
              >
                Sign In
              </Button>
            </div>
          </Card>
        )}

        {isEditor && (
          <Link to="/grocery-lists" className="text-callout text-primary underline">
            View in My Lists
          </Link>
        )}

        <div className="space-y-md">
          {uncheckedItems.length > 0 && (
            <div className="space-y-sm">
              <h3 className="text-callout font-semibold text-text-secondary px-md mb-md">
                To Buy ({uncheckedItems.length})
              </h3>
              {uncheckedItems.map(item => (
                <Card key={item.id} className="p-lg">
                  <div className="flex items-center gap-lg">
                    <button
                      onClick={() => toggleItem(item.id)}
                      disabled={!isEditor}
                      className="flex-shrink-0 w-7 h-7 rounded-full border-2 border-text-tertiary/30 hover:border-primary transition-colors flex items-center justify-center disabled:cursor-default"
                    >
                      <Circle size={14} className="text-text-tertiary" />
                    </button>
                    {item.productImageURL ? (
                      <img src={item.productImageURL} alt={item.name}
                        className="w-16 h-16 rounded-medium object-contain p-xs bg-gradient-to-br from-accent/5 to-primary/5 flex-shrink-0" />
                    ) : (
                      <div className="w-16 h-16 rounded-medium bg-gradient-to-br from-accent/5 to-primary/5 flex items-center justify-center flex-shrink-0">
                        <Package size={24} className="text-text-tertiary" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h4 className="text-callout font-semibold text-text-primary truncate">{item.name}</h4>
                      {item.quantity > 1 && (
                        <p className="text-caption text-text-tertiary mt-xs">Qty: {item.quantity}</p>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {checkedItems.length > 0 && (
            <div className="space-y-sm pt-xl">
              <h3 className="text-callout font-semibold text-text-secondary px-md mb-md">
                Checked ({checkedItems.length})
              </h3>
              {checkedItems.map(item => (
                <Card key={item.id} className="bg-surface/50 opacity-60 p-lg">
                  <div className="flex items-center gap-lg">
                    <button
                      onClick={() => toggleItem(item.id)}
                      disabled={!isEditor}
                      className="flex-shrink-0 w-7 h-7 rounded-full bg-success flex items-center justify-center disabled:cursor-default"
                    >
                      <Check size={16} className="text-white" />
                    </button>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-callout text-text-secondary line-through truncate">{item.name}</h4>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {items.length === 0 && (
            <Card className="text-center py-xxxl p-xl">
              <ShoppingCart size={36} className="text-primary mx-auto mb-lg" />
              <h3 className="text-title-3 text-primary mb-md">This list is empty</h3>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default SharedListView;
